import { NavigableModal } from '$components'
import { useNavigate, useParams } from 'react-router-dom'
import { CreateWorkingHoursForm, CreateWorkingHoursSchema, Input } from '../forms/working-hours'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { scheduleShowOptions, scheduleShowQueryKey, scheduleUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { DateHelper } from '$utils/date'
import { ToastHelper } from '$components'
import { ToastType } from '$components'

export function EditWorkingHoursModal() {
  const navigate = useNavigate()
  const { id: userId } = useParams()
  const queryClient = useQueryClient()

  const { data: scheduleData, isLoading } = useQuery({
    ...scheduleShowOptions({
      path: {
        userId: Number(userId),
      },
      query: {
        include_deleted: true,
      },
    }),
  })

  const schedule = scheduleData?.data ?? []

  const { mutate: updateSchedule, isPending: isUpdatingSchedule } = useMutation({
    ...scheduleUpdateMutation(),
    onSuccess: () => {
      ToastHelper.updateSuccess(ToastType.WorkingHours)
      queryClient.invalidateQueries({
        queryKey: scheduleShowQueryKey({
          path: {
            userId: Number(userId),
          },
        }),
      })
      handleClose()
    },
    onError: () => ToastHelper.updateError(ToastType.WorkingHours),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = (values: CreateWorkingHoursSchema) => {
    updateSchedule({
      path: {
        userId: Number(userId),
      },
      body: {
        days: values.working_hours.map((day) => ({
          id: day.id,
          day: Number(day.day),
          active_from: new Date().toISOString(),
          times: day.active
            ? day.times.map((time) => ({
              from: DateHelper.toHoursMinutes(time.from),
              to: DateHelper.toHoursMinutes(time.to),
            }))
            : [],
        })),
      },
    })
  }

  if (isLoading) {
    return null
  }

  return (
    <CreateWorkingHoursForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingSchedule}
      defaultValues={{
        [Input.WorkingHours]: schedule.map((schedule) => ({
          [Input.Id]: schedule.id,
          [Input.Day]: schedule.day,
          [Input.Active]: !!schedule.times.length,
          [Input.Times]: schedule.times.length > 0
            ? schedule.times.map((time) => ({
              [Input.From]: time.from,
              [Input.To]: time.to,
            }))
            : [
              {
                [Input.From]: '09:00',
                [Input.To]: '17:00',
              },
            ],
        })),
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Working Hours
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <CreateWorkingHoursForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <CreateWorkingHoursForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </CreateWorkingHoursForm>
  )
}
